/**
 * Admin Portal → Partners → Statement review — `/admin/partners/statements/:statementId`.
 *
 * The monthly routine: open a draft → review the flags and lines → Approve & send
 * (confirming the address and the email it will send) → later Mark paid. Plus the note
 * that prints on the statement (draft/approved only), Void & reissue (typed
 * confirmation), Resend (sent or paid; status unchanged) and the PDF/CSV downloads
 * (signed links requested on the click — they expire in five minutes and every request
 * is audited). Approve & send and Resend are refused when Coach Cedar's payer details
 * are not configured on the server: that refusal is shown as a backend setting, not a
 * screen problem. Spec: docs/handoff/PARTNERS_ADMIN_FE_HANDOFF.md §2.5, §3a.
 *
 * Web-only (the admin SPA): react-router is fine here.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AdminButton, AdminCard, SectionHeader } from '../../../components/admin/ui';
import { PartnerApiError, PartnersAdmin } from '../../../services/partnersAdminService';
import {
  approveResultMessage,
  flagsSummary,
  formatCents,
  isPayerIdentityRefusal,
  markPaidBody,
  noteError,
  planLabel,
  resendResultMessage,
  statementActions,
  statusBadge,
  type EmailPreview,
  type ReviewFlag,
  type StatementDetail,
} from './partnersLogic';
import {
  NoticeLine,
  ProgramBanner,
  ReadOnlyNote,
  StatusBadge,
  TypedConfirm,
  errText,
  fmtDate,
  fmtDateTime,
  hint,
  openPendingTab,
  input,
  label,
  row,
  td,
  th,
  useAdminRole,
  useBusy,
  useProgramStatus,
  type Notice,
} from './PartnersShared';

type Dialog = null | 'approve' | 'paid' | 'void' | 'resend';

export function AdminPartnerStatement() {
  const { statementId = '' } = useParams();
  const navigate = useNavigate();
  const { role, writable, loaded: roleLoaded } = useAdminRole();
  const program = useProgramStatus();
  const [st, setSt] = useState<StatementDetail | null>(null);
  const [flags, setFlags] = useState<ReviewFlag[] | null>(null);
  const [flagsError, setFlagsError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  // The signed link when the browser blocked the new tab: shown in place, never navigated to.
  const [blockedLink, setBlockedLink] = useState<{ which: 'pdf' | 'csv'; url: string } | null>(null);
  const [payerRefusal, setPayerRefusal] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [note, setNote] = useState('');
  const [paid, setPaid] = useState({ reference: '', on: '' });
  const [voidNote, setVoidNote] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, run] = useBusy();

  const load = useCallback(async () => {
    try {
      const res = await PartnersAdmin.getStatement(statementId);
      setSt(res.statement);
      setFlags(res.flags);
      setFlagsError(res.flags_error);
      setNote(res.statement.note ?? '');
    } catch (e) {
      setNotice({ kind: 'error', text: errText(e) });
    }
  }, [statementId]);

  useEffect(() => { load(); }, [load]);

  const loadPreview = useCallback(async () => {
    try {
      setPreview(await PartnersAdmin.emailPreview(statementId));
    } catch (e) {
      setPreview(null);
      setNotice({ kind: 'error', text: `Email preview: ${errText(e)}` });
    }
  }, [statementId]);

  const openDialog = (d: Dialog) => {
    setDialog(d);
    if (d === 'approve' || d === 'resend') loadPreview();
  };

  const onRefusal = (e: unknown) => {
    const msg = errText(e);
    if (isPayerIdentityRefusal(msg)) {
      setPayerRefusal(msg);
      setNotice(null);
    } else {
      setNotice({ kind: 'error', text: msg });
    }
  };

  const approve = () =>
    run(async () => {
      try {
        const res = await PartnersAdmin.approveSend(statementId, sendEmail);
        setDialog(null);
        setPayerRefusal(null);
        setNotice({ kind: res.emailed ? 'ok' : 'info', text: approveResultMessage(res) });
        await load();
      } catch (e) {
        setDialog(null);
        onRefusal(e);
      }
    });

  const resend = () =>
    run(async () => {
      try {
        const res = await PartnersAdmin.resend(statementId);
        setDialog(null);
        setPayerRefusal(null);
        setNotice({ kind: res.emailed ? 'ok' : 'info', text: resendResultMessage(res) });
        await load();
      } catch (e) {
        setDialog(null);
        onRefusal(e);
      }
    });

  const markPaid = () =>
    run(async () => {
      const { body, errors } = markPaidBody(paid.reference, paid.on);
      if (errors.length) { setNotice({ kind: 'error', text: 'Not marked paid:', extra: errors }); return; }
      try {
        const res = await PartnersAdmin.markPaid(statementId, body);
        setDialog(null);
        setNotice({ kind: 'ok', text: `Marked paid (${res.paid_reference}).` });
        await load();
      } catch (e) {
        setDialog(null);
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const voidIt = () =>
    run(async () => {
      const err = voidNote ? noteError(voidNote) : null;
      if (err) { setNotice({ kind: 'error', text: `Note: ${err}` }); return; }
      try {
        const res = await PartnersAdmin.voidReissue(statementId, voidNote.trim() || null);
        setDialog(null);
        if (res.reissued?.id) {
          setNotice({ kind: 'ok', text: `Voided. The entries moved to a new draft, ${res.reissued.number}.` });
          navigate(`/admin/partners/statements/${res.reissued.id}`);
        } else {
          setNotice({ kind: 'ok', text: res.reissued ? `Voided. The entries moved to a new draft, ${res.reissued.number}.` : 'Voided. Nothing to reissue.' });
          await load();
        }
      } catch (e) {
        setDialog(null);
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const saveNote = () =>
    run(async () => {
      const err = noteError(note);
      if (err) { setNotice({ kind: 'error', text: `Note: ${err}` }); return; }
      try {
        const res = await PartnersAdmin.setNote(statementId, note.trim() || null);
        setNotice({
          kind: 'ok',
          text: `Note ${res.note ? 'saved' : 'cleared'}.${res.stored_pdf_cleared ? ' The stored PDF was cleared: downloads answer "not stored" until the next Approve & send stores one with the note.' : ''}`,
        });
        await load();
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const download = (which: 'pdf' | 'csv') => {
    // The tab opens NOW, inside the click (a popup blocker allows that); the signed link
    // (five minutes, audited) is requested after and the tab is pointed at it — or closed
    // if the request fails.
    setBlockedLink(null);
    const point = openPendingTab((url) => setBlockedLink({ which, url }));
    let pointed = false;
    const aim = (url: string | null) => { pointed = true; point(url); };
    run(async () => {
      try {
        const res = await PartnersAdmin.statementFiles(statementId);
        aim(which === 'pdf' ? res.pdf_url : res.csv_url);
      } catch (e) {
        aim(null);
        if (e instanceof PartnerApiError && e.code === 'files_not_stored') {
          setNotice({ kind: 'info', text: e.message });
        } else {
          setNotice({ kind: 'error', text: errText(e) });
        }
      }
    }).then(() => {
      // A second click while one is in flight never ran: close its blank tab.
      if (!pointed) point(null);
    });
  };

  if (!st) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ fontSize: 13, marginBottom: 8 }}><Link to="/admin/partners">← Partners</Link></div>
        <NoticeLine notice={notice} />
        {!notice && <div>Loading…</div>}
      </div>
    );
  }

  const actions = statementActions(st.status);
  const to = st.statement_email || st.contact_email;
  const priorLines = st.lines.filter((l) => l.prior_period);
  const periodLines = st.lines.filter((l) => !l.prior_period);

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <div style={{ fontSize: 13, marginBottom: 8 }}>
        <Link to="/admin/partners">← Partners</Link> · <Link to={`/admin/partners/${st.partner_id}?tab=statements`}>{st.display_name}</Link>
      </div>
      <SectionHeader
        title={<>Statement {st.number} <StatusBadge status={st.status} tone={statusBadge(st.status)} /></>}
        subtitle={`${st.display_name} (${st.legal_name}) · period ${fmtDate(st.period_start)} – ${fmtDate(st.period_end)}`}
      />
      <ProgramBanner status={program.status} error={program.error} />
      <ReadOnlyNote writable={writable} loaded={roleLoaded} role={role} />
      {payerRefusal && (
        <div role="alert" style={{ background: 'var(--status-error-bg, #fef2f2)', color: 'var(--status-error-text, #991b1b)', padding: '12px 14px', borderRadius: 10, fontSize: 13, marginBottom: 12 }}>
          <b>Refused — nothing was stored or emailed.</b> Coach Cedar's payer details (legal name, address, tax number) must print on
          every statement that goes out, and the server does not have them yet. This is a backend setting, not something to fix on this
          screen — ask whoever manages the server configuration.
          <div style={{ marginTop: 6, fontSize: 12 }}>{payerRefusal}</div>
        </div>
      )}
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
        <AdminCard><div style={label}>Subtotal</div><b>{formatCents(st.subtotal_cents, st.currency)}</b></AdminCard>
        <AdminCard><div style={label}>Tax</div><b>{formatCents(st.tax_cents, st.currency)}</b></AdminCard>
        <AdminCard><div style={label}>Total</div><b style={{ fontSize: 18 }}>{formatCents(st.total_cents, st.currency)}</b>{st.total_cents < 0 && <div style={hint}>Negative: nothing is due from the partner and it is never sent (owner ruling 4: a negative month carries forward). Leave it as a draft.</div>}</AdminCard>
        <AdminCard>
          <div style={label}>Dates</div>
          <div style={{ fontSize: 12 }}>Statement {fmtDate(st.statement_date)} · due {fmtDate(st.due_date)} (net {st.net_days})</div>
          <div style={{ fontSize: 12 }}>Sent {fmtDateTime(st.sent_at)}{st.last_resent_at ? ` · last re-sent ${fmtDateTime(st.last_resent_at)}` : ''}</div>
          <div style={{ fontSize: 12 }}>Paid {fmtDateTime(st.paid_at)}{st.paid_reference ? ` (${st.paid_reference})` : ''}</div>
        </AdminCard>
      </div>

      <AdminCard style={{ marginBottom: 16 }}>
        <h4 style={{ margin: '0 0 6px', fontSize: 14 }}>Review flags</h4>
        <div style={{ fontSize: 13, color: flags === null ? 'var(--status-warning-text, #92400e)' : undefined }}>{flagsSummary(flags, flagsError)}</div>
        {flags && flags.map((f) => (
          <div key={`${f.code}-${f.message}`} style={{ fontSize: 13, padding: '6px 0', borderTop: '1px solid var(--border-subtle, #f3f4f6)' }}>
            <code>{f.code}</code> — {f.message}
          </div>
        ))}
      </AdminCard>

      <AdminCard style={{ marginBottom: 16 }}>
        <div style={row}>
          <AdminButton size="sm" variant="secondary" onClick={() => download('pdf')} disabled={busy}>Download PDF</AdminButton>
          <AdminButton size="sm" variant="secondary" onClick={() => download('csv')} disabled={busy}>Download CSV</AdminButton>
          <AdminButton size="sm" variant="ghost" onClick={() => { setShowPreview((v) => !v); if (!preview) loadPreview(); }}>
            {showPreview ? 'Hide' : 'Preview'} the partner email
          </AdminButton>
          <div style={{ flex: 1 }} />
          {writable && actions.approveSend && <AdminButton size="sm" onClick={() => openDialog('approve')} disabled={busy}>Approve &amp; send</AdminButton>}
          {writable && actions.markPaid && <AdminButton size="sm" variant="secondary" onClick={() => openDialog('paid')} disabled={busy}>Mark paid</AdminButton>}
          {writable && actions.resend && <AdminButton size="sm" variant="secondary" onClick={() => openDialog('resend')} disabled={busy}>Resend</AdminButton>}
          {writable && actions.voidReissue && <AdminButton size="sm" variant="danger" onClick={() => openDialog('void')} disabled={busy}>Void &amp; reissue</AdminButton>}
        </div>
        {blockedLink && (
          <div style={{ ...hint, marginTop: 8 }} data-testid="blocked-download">
            Your browser blocked the new tab.{' '}
            <a href={blockedLink.url} target="_blank" rel="noopener noreferrer">Open the {blockedLink.which.toUpperCase()}</a>
            {' '}(the link is valid for five minutes).
          </div>
        )}
        <div style={hint}>Download links are signed for five minutes and every request is audited — they are requested on the click.</div>
        {showPreview && <EmailPreviewBox preview={preview} />}
      </AdminCard>

      {actions.note && (
        <AdminCard style={{ marginBottom: 16 }}>
          <label style={label} htmlFor="stmt-note">Note — prints on the statement, under the totals</label>
          <textarea id="stmt-note" rows={2} style={{ ...input, width: '100%' }} disabled={!writable} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
          {writable && (
            <div style={{ ...row, marginTop: 6 }}>
              <AdminButton size="sm" variant="secondary" onClick={saveNote} disabled={busy || note === (st.note ?? '')}>Save note</AdminButton>
              {st.status === 'approved' && <span style={hint}>Changing the note on an approved statement clears its stored PDF until the next Approve &amp; send.</span>}
            </div>
          )}
        </AdminCard>
      )}
      {!actions.note && st.note && (
        <AdminCard style={{ marginBottom: 16 }}><div style={label}>Note (frozen — a change is a void &amp; reissue)</div><div style={{ fontSize: 13 }}>{st.note}</div></AdminCard>
      )}

      <AdminCard flush>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>Joined</th><th style={th}>Tag</th><th style={th}>Platform</th><th style={th}>Plan</th><th style={th}>Kind</th><th style={th}>Amount</th></tr></thead>
          <tbody>
            {st.lines.length === 0 && <tr><td style={td} colSpan={6}>No lines.</td></tr>}
            {periodLines.map((l) => (
              <tr key={l.id}>
                <td style={td}>{fmtDate(l.join_date)}</td><td style={td}>{l.tag}</td><td style={td}>{l.platform ?? '—'}</td>
                <td style={td}>{planLabel(l.billing_period)}</td>
                <td style={td}>{l.kind}</td><td style={td}>{formatCents(l.amount_cents, st.currency)}</td>
              </tr>
            ))}
            {priorLines.length > 0 && (
              <tr><td style={{ ...td, fontStyle: 'italic', fontWeight: 600 }} colSpan={6}>Prior-period lines: entries from earlier months, billed on this statement</td></tr>
            )}
            {priorLines.map((l) => (
              <tr key={l.id}>
                <td style={td}>{fmtDate(l.join_date)}</td><td style={td}>{l.tag}</td><td style={td}>{l.platform ?? '—'}</td>
                <td style={td}>{planLabel(l.billing_period)}</td>
                <td style={td}>{l.kind} ({l.prior_period_month})</td><td style={td}>{formatCents(l.amount_cents, st.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </AdminCard>

      {dialog === 'approve' && (
        <ConfirmBox title={`Approve & send ${st.number}?`} onCancel={() => setDialog(null)} onConfirm={approve} busy={busy} confirmLabel={sendEmail ? 'Approve & send' : 'Approve only'}>
          <p>Total <b>{formatCents(st.total_cents, st.currency)}</b>, due {fmtDate(st.due_date)}. The PDF and CSV are rendered and stored now; after this the statement is frozen.</p>
          <p>{to ? <>It will be emailed to <b>{to}</b>{st.statement_email ? '' : ' (the contact email — no statement email is set)'}.</> : <b>No statement or contact email is set — it will be approved but not emailed.</b>}</p>
          <label style={{ fontSize: 13 }}><input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} /> Email it to the partner now</label>
          <EmailPreviewBox preview={preview} />
        </ConfirmBox>
      )}
      {dialog === 'resend' && (
        <ConfirmBox title={`Resend ${st.number}?`} onCancel={() => setDialog(null)} onConfirm={resend} busy={busy} confirmLabel="Resend">
          <p>The same statement, re-rendered from its frozen lines, emailed again to <b>{to ?? '(no address)'}</b>. The status stays <b>{st.status}</b>; "sent" keeps its first date. A second resend within two minutes is refused.</p>
          <EmailPreviewBox preview={preview} />
        </ConfirmBox>
      )}
      {dialog === 'paid' && (
        <TypedConfirm
          title={`Mark ${st.number} paid?`}
          danger={false}
          body={
            <div style={{ display: 'grid', gap: 8 }}>
              <div>Record the payment of {formatCents(st.total_cents, st.currency)}. A paid statement can never be voided.</div>
              <div style={row}>
                <div>
                  <label style={label} htmlFor="pd-ref">Payment reference</label>
                  <input id="pd-ref" style={input} value={paid.reference} onChange={(e) => setPaid({ ...paid, reference: e.target.value })} />
                </div>
                <div>
                  <label style={label} htmlFor="pd-on">Paid on (blank = now)</label>
                  <input id="pd-on" type="date" style={input} value={paid.on} onChange={(e) => setPaid({ ...paid, on: e.target.value })} />
                </div>
              </div>
            </div>
          }
          expected="PAID"
          confirmLabel="Mark paid"
          busy={busy}
          onConfirm={markPaid}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog === 'void' && (
        <TypedConfirm
          title={`Void ${st.number} and reissue?`}
          body={
            <div style={{ display: 'grid', gap: 8 }}>
              <div>The statement becomes void (kept, never deleted). Its entries move to a NEW draft with the next number, which you review and send like any draft.{st.status === 'sent' ? ' The partner already has this one — tell them it is replaced.' : ''}</div>
              <div>
                <label style={label} htmlFor="vd-note">Note for the new draft (optional, prints on it)</label>
                <input id="vd-note" style={{ ...input, width: '100%' }} value={voidNote} onChange={(e) => setVoidNote(e.target.value)} />
              </div>
            </div>
          }
          expected={st.number}
          confirmLabel="Void & reissue"
          busy={busy}
          onConfirm={voidIt}
          onCancel={() => setDialog(null)}
        />
      )}
    </div>
  );
}

const ConfirmBox: React.FC<{ title: string; confirmLabel: string; busy: boolean; onConfirm: () => void; onCancel: () => void; children: React.ReactNode }> = ({
  title, confirmLabel, busy, onConfirm, onCancel, children,
}) => (
  <div role="dialog" aria-modal="true" aria-label={title}
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
    <div style={{ background: 'var(--surface, #fff)', borderRadius: 12, padding: 20, width: 'min(640px, 94vw)', maxHeight: '90vh', overflow: 'auto', fontSize: 13 }}>
      <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>{title}</h3>
      {children}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
        <AdminButton variant="secondary" size="sm" onClick={onCancel} disabled={busy}>Cancel</AdminButton>
        <AdminButton size="sm" onClick={onConfirm} disabled={busy}>{busy ? 'Working…' : confirmLabel}</AdminButton>
      </div>
    </div>
  </div>
);

/**
 * The email approve & send / resend would send, as the server builds it. The HTML part
 * is shown in a sandboxed frame (no scripts, no same-origin): the partner's name in it
 * was typed by an admin and is escaped by the server, and the frame keeps it inert anyway.
 */
const EmailPreviewBox: React.FC<{ preview: EmailPreview | null }> = ({ preview }) => {
  if (!preview) return <div style={{ ...hint, marginTop: 8 }}>Loading the email preview…</div>;
  return (
    <div style={{ marginTop: 10, border: '1px solid var(--border, #e5e7eb)', borderRadius: 8, padding: 10, fontSize: 12 }}>
      <div><b>To:</b> {preview.to_email ?? '— (no address: it would not be emailed)'}{preview.to_source === 'contact_email' ? ' (contact email)' : ''}</div>
      <div><b>Subject:</b> {preview.subject}</div>
      {preview.reply_to && <div><b>Reply-to:</b> {preview.reply_to}</div>}
      <div><b>Attachments:</b> {preview.attachments.join(', ')}</div>
      {preview.html ? (
        <iframe title="Email preview" sandbox="" srcDoc={preview.html} style={{ width: '100%', height: 170, border: 'none', background: '#fff', marginTop: 6 }} />
      ) : (
        <div style={{ marginTop: 6 }}>No body to preview ({preview.reason}).</div>
      )}
    </div>
  );
};

export default AdminPartnerStatement;
